import React, { useState, useMemo } from 'react';
import Icon from 'components/AppIcon';
import EmptyState from 'components/EmptyState';

const ALL_SYMBOLS = ['LME_CU', 'LME_AL', 'LME_ZN', 'LME_NI', 'LME_PB', 'EUR_USD', 'REG_INOX', 'REG_FE', 'MAN_BRZ', 'REG_LAT'];
const PAGE_SIZE = 50;

const TIME_RANGES = [
  { label: '1h',  ms: 1 * 60 * 60 * 1000 },
  { label: '6h',  ms: 6 * 60 * 60 * 1000 },
  { label: '24h', ms: 24 * 60 * 60 * 1000 },
  { label: '7d',  ms: 7 * 24 * 60 * 60 * 1000 },
  { label: 'Todo', ms: null },
];

export default function MarketHistoryTable({ ticks }) {
  const [symbolFilter, setSymbolFilter] = useState('all');
  const [timeRange, setTimeRange] = useState('24h');
  const [page, setPage] = useState(0);
  const [sortDir, setSortDir] = useState('desc');

  const filtered = useMemo(() => {
    let data = [...(ticks || [])];

    if (symbolFilter !== 'all') {
      data = data?.filter(t => t?.symbol === symbolFilter);
    }

    const range = TIME_RANGES?.find(r => r?.label === timeRange);
    if (range?.ms) {
      const cutoff = new Date(Date.now() - range.ms);
      data = data?.filter(t => new Date(t.ts) >= cutoff);
    }

    data?.sort((a, b) => {
      const diff = new Date(b.ts) - new Date(a.ts);
      return sortDir === 'desc' ? diff : -diff;
    });

    return data;
  }, [ticks, symbolFilter, timeRange, sortDir]);

  const totalPages = Math.ceil(filtered?.length / PAGE_SIZE);
  const pageData = filtered?.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Compute change vs previous tick per symbol
  const prevBySymbol = useMemo(() => {
    const map = {};
    const sorted = [...(ticks || [])]?.sort((a, b) => new Date(b.ts) - new Date(a.ts));
    for (const t of sorted) {
      if (!map?.[t?.symbol]) { map[t.symbol] = []; }
      map?.[t?.symbol]?.push(t);
    }
    return map;
  }, [ticks]);

  const getChange = (tick) => {
    const arr = prevBySymbol?.[tick?.symbol] || [];
    const idx = arr?.findIndex(t => t?.id === tick?.id);
    if (idx < 0 || idx >= arr?.length - 1) return null;
    const prev = arr?.[idx + 1];
    return parseFloat(tick?.value) - parseFloat(prev?.value);
  };

  const getEmptyState = () => {
    const totalTicks = (ticks || [])?.length;
    const hasSymbolFilter = symbolFilter !== 'all';
    const hasTimeFilter = timeRange !== 'Todo';

    if (totalTicks === 0) {
      return (
        <EmptyState
          icon="Signal"
          title="Sin datos de mercado"
          description="No hay precios registrados todavía. Introduce el primer precio manualmente o conecta un feed de datos."
          ctaLabel="Añadir precio manualmente"
          colSpan={6}
        />
      );
    }
    // Filters active but no results
    return (
      <EmptyState
        icon="Calendar"
        title="Sin registros en este período"
        description="No hay ticks de mercado para el símbolo o rango seleccionado."
        ctaLabel="Limpiar filtros"
        ctaAction={() => { setSymbolFilter('all'); setTimeRange('Todo'); setPage(0); }}
        colSpan={6}
      />
    );
  };

  return (
    <div className="bg-card border border-border rounded-md" style={{ boxShadow: 'var(--shadow-sm)' }}>
      <div className="flex flex-wrap items-center gap-3 p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Icon name="Table" size={16} color="var(--color-accent)" />
          <h3 className="text-sm font-semibold text-foreground font-caption uppercase tracking-wider">Historial de Mercado</h3>
        </div>
        <div className="flex items-center gap-2 ml-auto flex-wrap">
          {/* Symbol filter */}
          <select
            value={symbolFilter}
            onChange={e => { setSymbolFilter(e?.target?.value); setPage(0); }}
            className="text-xs border border-border rounded px-2 py-1.5 bg-background text-foreground font-data"
          >
            <option value="all">Todos los símbolos</option>
            {ALL_SYMBOLS?.map(s => <option key={s} value={s}>{s}</option>)}
          </select>

          {/* Time range */}
          <div className="flex rounded overflow-hidden border border-border">
            {TIME_RANGES?.map(r => (
              <button
                key={r?.label}
                onClick={() => { setTimeRange(r?.label); setPage(0); }}
                className={`px-2.5 py-1 text-xs font-caption transition-all ${
                  timeRange === r?.label
                    ? 'bg-accent text-white' :'bg-background text-muted-foreground hover:bg-muted'
                }`}
              >
                {r?.label}
              </button>
            ))}
          </div>

          {/* Sort toggle */}
          <button
            onClick={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')}
            className="flex items-center gap-1 px-2.5 py-1 text-xs border border-border rounded bg-background text-muted-foreground hover:text-foreground transition-all"
          >
            <Icon name={sortDir === 'desc' ? 'ArrowDown' : 'ArrowUp'} size={12} color="currentColor" />
            Fecha
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="text-left px-4 py-2.5 text-muted-foreground font-caption">Timestamp</th>
              <th className="text-left px-4 py-2.5 text-muted-foreground font-caption">Símbolo</th>
              <th className="text-right px-4 py-2.5 text-muted-foreground font-caption">Valor</th>
              <th className="text-right px-4 py-2.5 text-muted-foreground font-caption">Cambio</th>
              <th className="text-center px-4 py-2.5 text-muted-foreground font-caption">Moneda</th>
              <th className="text-center px-4 py-2.5 text-muted-foreground font-caption">Fuente</th>
            </tr>
          </thead>
          <tbody>
            {pageData?.length === 0 ? (
              getEmptyState()
            ) : (
              pageData?.map(tick => {
                const change = getChange(tick);
                const isUp = change !== null ? change >= 0 : null;
                return (
                  <tr key={tick?.id} className="border-b border-border/50 hover:bg-muted/30 transition-all">
                    <td className="px-4 py-2 text-muted-foreground font-data">
                      {new Date(tick.ts)?.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </td>
                    <td className="px-4 py-2">
                      <span className="px-2 py-0.5 rounded text-xs font-data font-medium"
                        style={{ background: 'var(--color-muted)', color: 'var(--color-foreground)' }}>
                        {tick?.symbol}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right text-foreground font-data font-medium">
                      {parseFloat(tick?.value)?.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                    </td>
                    <td className="px-4 py-2 text-right font-data">
                      {change !== null ? (
                        <span className={isUp ? 'text-success' : 'text-error'}>
                          {isUp ? '+' : ''}{change?.toFixed(2)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-center">
                      <span className="px-1.5 py-0.5 rounded text-xs font-caption"
                        style={{ background: 'var(--color-muted)', color: 'var(--color-muted-foreground)' }}>
                        {tick?.currency}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <span className="px-1.5 py-0.5 rounded text-xs font-caption"
                        style={{
                          background: tick?.source === 'MANUAL' ? 'rgba(234,88,12,0.1)' : 'var(--color-muted)',
                          color: tick?.source === 'MANUAL' ? 'var(--color-accent)' : 'var(--color-muted-foreground)'
                        }}>
                        {tick?.source}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-border">
          <span className="text-xs text-muted-foreground font-data">
            {filtered?.length} registros · Página {page + 1} de {totalPages}
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-2.5 py-1 text-xs border border-border rounded disabled:opacity-40 hover:bg-muted transition-all"
            >
              <Icon name="ChevronLeft" size={12} color="currentColor" />
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="px-2.5 py-1 text-xs border border-border rounded disabled:opacity-40 hover:bg-muted transition-all"
            >
              <Icon name="ChevronRight" size={12} color="currentColor" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
